<template>
	<div class="deposit-modal _modal">
		<div class="_modal-overlay" @click.self="closeDepositModal"></div>
		<div class="deposit-modal__inner _modal-inner">
			<div class="_modal-close" @click="closeDepositModal"></div>
			<div class="deposit-modal__content">
				<div class="deposit-modal__top">
					<div class="deposit-modal__title _h3">
						{{ $t('Payment details') }}
					</div>
					<div class="deposit-modal__subtitle">
						<span>{{ $t('Credit/Debit Card') }}</span>
						<span> {{ $t('Amount') }}:</span>
						<span>{{ depositAmount }} {{ currencySymbol }}</span>
					</div>
				</div>
				<div class="deposit-modal__inputs">
					<BaseInput
						v-model="form.name"
						type="text"
						:label="`${$t('Name')}:`"
						class="deposit-modal__input"
					/>
					<BaseInput
						v-model="form.surname"
						type="text"
						:label="`${$t('Surname')}:`"
						class="deposit-modal__input"
					/>
					<BaseInput
						v-model="form.phone"
						type="text"
						:label="`${$t('Phone')}:`"
						class="deposit-modal__input"
					/>
					<div class="deposit-modal__input">
						<div class="deposit-modal__select-label _select-label-input">
							{{ `${$t('Country')}:` }}
						</div>
						<select
							class="deposit-modal__select _select-input"
							v-model="form.countryId"
						>
							<option
								v-for="option in countryOptions"
								:key="option.id"
								:value="option.id"
							>
								{{ option.title }}
							</option>
						</select>
					</div>
					<BaseInput
						v-model="form.city"
						type="text"
						:label="`${$t('City')}:`"
						class="deposit-modal__input"
					/>
					<BaseInput
						v-model="form.address"
						type="text"
						:label="`${$t('Address')}:`"
						class="deposit-modal__input"
					/>
					<BaseInput
						v-model="form.postCode"
						type="text"
						:label="`${$t('Post code')}:`"
						class="deposit-modal__input"
					/>
				</div>
				<BaseTermCheckbox v-model="terms" class="deposit-modal__checkbox">
					{{ $t('I agree with') }}
					<router-link
						v-if="termsPage?.slug"
						:to="{ name: 'TextPage', params: { slug: termsPage.slug } }"
						class="desc small"
						target="_blank"
					>
						{{ termsPage.title }}
					</router-link>
					<template v-if="termsPage?.slug && privacyPage?.slug">
						<span class="desc small">{{ ' , ' }}</span>
						<router-link
							:to="{
								name: 'TextPage',
								params: { slug: privacyPage.slug },
							}"
							class="desc small"
							target="_blank"
						>
							{{ privacyPage.title }}
						</router-link>
					</template>
				</BaseTermCheckbox>
				<div class="deposit-modal__bottom">
					<Transition>
						<div v-if="error" class="deposit-modal__error">
							{{ error }}
						</div>
					</Transition>
					<div class="deposit-modal__actions">
						<BaseButton
							class="deposit-modal__button _btn-primary"
							:disabled="!requiredFieldsAreFilled || isSubmitting"
							@click="submitDeposit"
						>
							<span v-if="isSubmitting">{{ $t('Submiting') }}...</span>
							<span v-else>{{ $t('Submit') }}</span>
						</BaseButton>
						<BaseButton
							class="deposit-modal__button _btn-gray-primary"
							@click="closeDepositModal"
						>
							<span>{{ $t('Back') }}</span>
						</BaseButton>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { PRIVACY_PAGE, TERMS_PAGE } from '@/store/modules/staticPages';
import { mapActions, mapGetters } from 'vuex';
import BaseButton, { ButtonThemes } from './base/BaseButton.vue';
import BaseInput from './base/BaseInput.vue';
import BaseTermCheckbox from './base/BaseTermCheckbox.vue';

export default {
	name: 'DepositModal',
	components: { BaseInput, BaseTermCheckbox, BaseButton },
	props: {
		user: Object,
		loaderIsVisible: {
			type: Boolean,
			required: true,
		},
		depositAmount: {
			type: Number,
			required: true,
		},
		depositCurrency: {
			type: String,
			default: '€',
		},
		paymentType: {
			type: String,
			default: 'card',
		},
	},
	data() {
		return {
			ButtonThemes,
			isSubmitting: false,
			terms: false,
			error: null,
			countryOptions: [],
			form: {
				name: '',
				surname: '',
				phone: '',
				countryId: '',
				city: '',
				address: '',
				postCode: '',
			},
		};
	},
	computed: {
		...mapGetters('user', ['userData', 'userBalance']),
		...mapGetters('app', ['currencySymbol', 'currencyCode']),
		...mapGetters('staticPages', ['getTermsPage', 'getPrivacyPage']),
		termsPage() {
			return this.getTermsPage;
		},
		privacyPage() {
			return this.getPrivacyPage;
		},
		requiredFieldsAreFilled() {
			return (
				this.form.name &&
				this.form.surname &&
				this.form.phone &&
				this.form.countryId &&
				this.form.city &&
				this.form.address &&
				this.form.postCode &&
				this.terms
			);
		},
	},
	watch: {
		userData: {
			immediate: true,
			handler(val) {
				if (!val) return;

				const isPlaceholder = (v, placeholder) =>
					String(v ?? '').trim() === placeholder;

				if (!this.form.name) {
					const n = val.name || '';
					this.form.name = isPlaceholder(n, 'Name') ? '' : n;
				}

				if (!this.form.surname) {
					const s = val.surname || '';
					this.form.surname = isPlaceholder(s, 'Surname') ? '' : s;
				}

				if (!this.form.phone) this.form.phone = val.phone || '';
				if (!this.form.city) this.form.city = val.city || '';
				if (!this.form.address) this.form.address = val.address || '';
				if (!this.form.postCode) this.form.postCode = val.zip || '';
			},
		},
	},
	created() {
		this.TERMS_PAGE = TERMS_PAGE;
		this.PRIVACY_PAGE = PRIVACY_PAGE;
	},
	mounted() {
		this.isSubmitting = false;
		this.getCountries();
	},
	methods: {
		...mapActions('modals', ['closeDepositModal']),
		...mapActions('user', ['fetchUserData']),
		async getCountries() {
			try {
				const response = await this.$http.get(
					`${process.env.VUE_APP_API}countries`
				);
				this.countryOptions = response.data.payload;

				if (this.userData?.country) {
					const match = this.countryOptions.find(
						(c) => String(c.id) === String(this.userData.country)
					);
					if (match) {
						this.form.countryId = match.id;
					}
				}

				//if (!this.form.countryId && this.countryOptions.length) {
				//	this.form.countryId = this.countryOptions[0].id;
				//}
			} catch (error) {
				console.error('Failed to fetch countries:', error);
			}
		},

		async submitDeposit() {
			if (!this.requiredFieldsAreFilled || this.isSubmitting) return;

			this.isSubmitting = true;
			this.error = null;

			const payload = {
				name: this.form.name,
				surname: this.form.surname,
				phone: this.form.phone,
				country: this.form.countryId,
				city: this.form.city,
				address: this.form.address,
				postCode: this.form.postCode,
				amount: this.depositAmount,
				paymentType: this.paymentType || 'card',
				currency: this.currencyCode,
			};

			try {
				const response = await this.$http.post(
					`${process.env.VUE_APP_API}deposit/checkout`,
					payload
				);

				const data = response.data;

				if (data.status === 'ERROR') {
					this.error = data.message || this.$t('An error occurred.');
					this.$toast?.error(
						this.$t(data.message || 'An error occurred.')
					);
					this.isSubmitting = false;
					return;
				}

				if (data.redirect_url || data.redirectUrl) {
					if (this.fetchUserData) {
						this.fetchUserData();
					}
					const url = data.redirect_url || data.redirectUrl;
					window.location = url;
					return;
				}

				if (typeof data.balance !== 'undefined') {
					if (this.fetchUserData) this.fetchUserData();
					this.$toast?.success(this.$t('Deposit completed successfully.'));
					this.closeDepositModal();
				} else {
					this.$toast?.success(
						this.$t('Deposit initialized successfully.')
					);
					this.closeDepositModal();
				}
			} catch (error) {
				console.error('Error submitting purchase:', error);
				this.error =
					error?.response?.data?.message ||
					this.$t('Error while processing purchase.');
				this.$toast?.error(this.error);
			} finally {
				this.isSubmitting = false;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@use '@/assets/styles/mixins' as *;
@use '@/assets/styles/media' as *;
@use '@/assets/styles/components/classes' as *;

.deposit-modal {
	&__inner {
		max-width: 466px;
		@include adaptiveValue('padding-left', 60, 10);
		@include adaptiveValue('padding-right', 60, 10);
	}
	&__content {
	}
	&__top {
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 30, 15);
		}
	}
	&__title {
		display: block;
		text-align: center;
		@include adaptiveValue('font-size', 32, 21);
		line-height: 120%;
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 15, 10);
		}
	}
	&__subtitle {
		display: flex;
		justify-content: center;
		flex-wrap: wrap;
		gap: 6px;
		font-size: 12px;
		color: var(--secondary-color);
		line-height: 120%;
		font-weight: 400;
	}
	&__inputs {
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 20, 15);
		}
	}
	&__input {
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 15, 10);
		}
	}
	&__checkbox {
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 30, 15);
		}
	}
	&__bottom {
	}
	&__error {
		font-family: var(--font-family-inter);
		font-size: 16px;
		line-height: 120%;
		font-weight: 500;
		display: block;
		text-align: center;
		margin-bottom: 8px;
		color: var(--error-color);
	}
	&__actions {
	}
	&__button {
		max-width: 224px;
		margin-left: auto;
		margin-right: auto;
		width: 100%;
		&:not(:last-child) {
			@include adaptiveValue('margin-bottom', 15, 6);
		}
	}
}
</style>
